/**
 * Runtime theming for the storefront.
 *
 * Tailwind compiles utility classes (`bg-brand-500`, `text-brand-700`, …) with
 * baked-in hex values, so simply mutating CSS variables wouldn't recolor them.
 * Instead we inject — once — a `<style>` block that *re-maps* those utilities
 * to CSS variables when they live inside a `[data-store-theme]` subtree.
 * Each theme then just supplies the variable values via inline style.
 *
 * Limitation: tailwind opacity variants like `bg-brand-500/30` are compiled
 * with the original color and aren't re-mapped here — they keep the base
 * palette. Solid utilities (the bulk of the styling) recolor correctly.
 */

import { findStoreTheme, STORE_THEMES } from './storeThemes';

const STYLE_TAG_ID = 'store-theme-overrides';
const BRAND_SHADES  = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900];
const SECONDARY_SHADES = [500, 600];
const SURFACE_KEYS = ['', '-base', '-muted', '-sunken', '-inverse'];
const INK_KEYS     = ['', '-strong', '-muted', '-subtle', '-faint', '-inverse'];
const LINE_KEYS    = ['', '-subtle', '-strong'];

const buildOverrideCSS = () => {
    const lines = [];
    const root = '[data-store-theme]';

    // Brand + mekomit-alias shades — bg / text / border / ring / from / to / via
    for (const shade of BRAND_SHADES) {
        const v = `var(--st-brand-${shade})`;
        lines.push(
            `${root} .bg-brand-${shade}{background-color:${v}}`,
            `${root} .text-brand-${shade}{color:${v}}`,
            `${root} .border-brand-${shade}{border-color:${v}}`,
            `${root} .ring-brand-${shade}{--tw-ring-color:${v}}`,
            `${root} .from-brand-${shade}{--tw-gradient-from:${v} var(--tw-gradient-from-position);--tw-gradient-to:rgb(0 0 0 / 0) var(--tw-gradient-to-position);--tw-gradient-stops:var(--tw-gradient-from), var(--tw-gradient-to)}`,
            `${root} .to-brand-${shade}{--tw-gradient-to:${v} var(--tw-gradient-to-position)}`,
            `${root} .via-brand-${shade}{--tw-gradient-to:rgb(0 0 0 / 0) var(--tw-gradient-to-position);--tw-gradient-stops:var(--tw-gradient-from), ${v} var(--tw-gradient-via-position), var(--tw-gradient-to)}`,
            // mekomit-* legacy alias maps to the same brand variable
            `${root} .bg-mekomit-${shade}{background-color:${v}}`,
            `${root} .text-mekomit-${shade}{color:${v}}`,
            `${root} .border-mekomit-${shade}{border-color:${v}}`,
            `${root} .ring-mekomit-${shade}{--tw-ring-color:${v}}`,
            `${root} .from-mekomit-${shade}{--tw-gradient-from:${v} var(--tw-gradient-from-position);--tw-gradient-to:rgb(0 0 0 / 0) var(--tw-gradient-to-position);--tw-gradient-stops:var(--tw-gradient-from), var(--tw-gradient-to)}`,
            `${root} .to-mekomit-${shade}{--tw-gradient-to:${v} var(--tw-gradient-to-position)}`,
        );
    }

    // Secondary shades — 500/600 only (the rest are rarely used and keep their compiled values)
    for (const shade of SECONDARY_SHADES) {
        const v = `var(--st-secondary-${shade})`;
        lines.push(
            `${root} .bg-secondary-${shade}{background-color:${v}}`,
            `${root} .text-secondary-${shade}{color:${v}}`,
            `${root} .border-secondary-${shade}{border-color:${v}}`,
            `${root} .ring-secondary-${shade}{--tw-ring-color:${v}}`,
            `${root} .from-secondary-${shade}{--tw-gradient-from:${v} var(--tw-gradient-from-position);--tw-gradient-to:rgb(0 0 0 / 0) var(--tw-gradient-to-position);--tw-gradient-stops:var(--tw-gradient-from), var(--tw-gradient-to)}`,
            `${root} .to-secondary-${shade}{--tw-gradient-to:${v} var(--tw-gradient-to-position)}`,
        );
    }

    // Surface backgrounds
    for (const key of SURFACE_KEYS) {
        const v = `var(--st-surface${key})`;
        const cls = key === '' ? 'bg-surface' : `bg-surface${key}`;
        lines.push(`${root} .${cls}{background-color:${v}}`);
    }
    // text-ink shades
    for (const key of INK_KEYS) {
        const v = `var(--st-ink${key})`;
        const cls = key === '' ? 'text-ink' : `text-ink${key}`;
        lines.push(`${root} .${cls}{color:${v}}`);
    }
    // border-line shades
    for (const key of LINE_KEYS) {
        const v = `var(--st-line${key})`;
        const cls = key === '' ? 'border-line' : `border-line${key}`;
        lines.push(`${root} .${cls}{border-color:${v}}`);
    }

    // Make the wrapper itself adopt the themed surface + ink even when
    // the root element doesn't have an explicit bg-surface-base utility.
    lines.push(
        `${root}{background-color:var(--st-surface-base);color:var(--st-ink)}`,
    );

    return lines.join('\n');
};

let injected = false;
const ensureOverridesInjected = () => {
    if (injected || typeof document === 'undefined') return;
    if (document.getElementById(STYLE_TAG_ID)) { injected = true; return; }
    const style = document.createElement('style');
    style.id = STYLE_TAG_ID;
    style.textContent = buildOverrideCSS();
    document.head.appendChild(style);
    injected = true;
};

/**
 * Build the inline `style` object (CSS-variable values) for a given theme id.
 * Returns `{}` for unknown ids — the storefront then keeps its default look.
 */
export const getStoreThemeStyle = (themeId) => {
    ensureOverridesInjected();
    const theme = findStoreTheme(themeId);
    if (!theme) return {};
    const out = {};
    for (const [tokenKey, hex] of Object.entries(theme.tokens)) {
        out[`--st-${tokenKey}`] = hex;
    }
    return out;
};

/**
 * Build a small inline-style object for previewing a theme's anchor colors
 * (used by the picker tiles in the admin).
 */
export const getStoreThemeSwatch = (themeId) => {
    const theme = findStoreTheme(themeId);
    if (!theme) return null;
    const t = theme.tokens;
    return {
        brand: t['brand-500'],
        brandDark: t['brand-700'],
        secondary: t['secondary-500'],
        surface: t['surface'],
        surfaceBase: t['surface-base'],
        ink: t['ink'],
    };
};

export { STORE_THEMES, findStoreTheme };
